import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Icon } from "./icon";
import type { ComponentProps } from "react";

import { Button } from "./button";
import { SURFACE_SCRIM_CLASS } from "./overlay";
import { cn } from "./utils";

const Dialog = ({ ...props }: DialogPrimitive.Root.Props) => {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
};

const DialogTrigger = ({ ...props }: DialogPrimitive.Trigger.Props) => {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
};

const DialogPortal = ({ ...props }: DialogPrimitive.Portal.Props) => {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
};

const DialogClose = ({ ...props }: DialogPrimitive.Close.Props) => {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
};

const DialogOverlay = ({ className, ...props }: DialogPrimitive.Backdrop.Props) => {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        // Scrim matches the design prototype's centred-modal backdrop:
        // `bg-foreground/40` with NO backdrop blur. Drawers use a lighter
        // `bg-foreground/30` (see sheet.tsx) — that split is intentional.
        // SURFACE_SCRIM_CLASS (`z-[200]`) clears a detail takeover at `z-[100]`.
        `fixed inset-0 isolate ${SURFACE_SCRIM_CLASS} bg-foreground/40 duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0`,
        className,
      )}
      {...props}
    />
  );
};

const DIALOG_CHROME_CLASS =
  `${SURFACE_SCRIM_CLASS} grid w-full max-w-[calc(100%-2rem)] gap-(--density-field-gap) overflow-hidden rounded-lg bg-background p-(--density-card-pad) text-sm ring-1 ring-foreground/10 duration-100 outline-hidden sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95`;

const DIALOG_PLAIN_CLASS = `${SURFACE_SCRIM_CLASS} outline-none duration-150 data-open:animate-in data-open:zoom-in-95`;

const DialogContent = ({
  className,
  children,
  overlayClassName,
  variant = "default",
  showCloseButton,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  overlayClassName?: string;
  /** `plain` is position + z only — hosts that own size, colour, and close. */
  variant?: "default" | "plain";
}) => {
  const close = showCloseButton ?? variant !== "plain";
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        data-variant={variant}
        // Hosts that omit DialogDescription would otherwise warn. Callers can
        // still pass their own aria-describedby via props.
        aria-describedby={undefined}
        className={cn(
          "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
          variant === "plain" ? DIALOG_PLAIN_CLASS : DIALOG_CHROME_CLASS,
          className,
        )}
        {...props}
      >
        {children}
        {close && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
          >
            <Icon name="close" size="sm" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
};

const DialogHeader = ({ className, ...props }: ComponentProps<"div">) => {
  return (
    <div data-slot="dialog-header" className={cn("flex flex-col gap-2", className)} {...props} />
  );
};

const DialogFooter = ({
  className,
  showCloseButton = false,
  children,
  ...props
}: ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) => {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-(--density-card-pad) -mb-(--density-card-pad) flex flex-col-reverse gap-2 border-t border-border/50 bg-muted/30 p-(--density-card-pad) sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
      )}
    </div>
  );
};

const DialogTitle = ({ className, ...props }: DialogPrimitive.Title.Props) => {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-base leading-none font-medium", className)}
      {...props}
    />
  );
};

const DialogDescription = ({ className, ...props }: DialogPrimitive.Description.Props) => {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
};

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
